import { AlertTriangle, Loader2, Pause, Play, RefreshCw } from 'lucide-react';

import { cn } from '@/utils';

interface VideoOverlaysProps {
  isError: boolean;
  isBuffering: boolean;
  isPlaying: boolean;
  showControls: boolean;
  onRetry: () => void;
  onPlayPause: () => void;
}

export const VideoOverlays = ({
  isError,
  isBuffering,
  isPlaying,
  showControls,
  onRetry,
  onPlayPause,
}: VideoOverlaysProps) => {
  if (isError) {
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/80 px-6 text-center">
        <AlertTriangle className="h-10 w-10 text-white/80" />
        <p className="text-white text-sm md:text-base font-medium">
          We couldn&apos;t load this video.
        </p>
        <p className="text-white/60 text-xs md:text-sm max-w-xs">
          Check your connection and try again.
        </p>
        <button
          onClick={onRetry}
          className="mt-1 inline-flex items-center gap-2 rounded-full bg-white/15 hover:bg-white/25 transition-colors px-4 py-2 text-white text-sm font-medium"
        >
          <RefreshCw className="h-4 w-4" />
          Retry
        </button>
      </div>
    );
  }

  return (
    <>
      {/* Buffering spinner */}
      {isBuffering ? (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <Loader2 className="h-10 w-10 md:h-14 md:w-14 text-white/90 animate-spin" />
        </div>
      ) : null}

      {/* Play/pause overlay (hidden while buffering) */}
      <button
        className={cn(
          'absolute inset-0 flex items-center justify-center bg-transparent border-none transition-opacity pb-14 md:pb-2',
          showControls && !isBuffering ? 'opacity-100' : 'opacity-0'
        )}
        onClick={onPlayPause}
        aria-label={isPlaying ? 'Pause video' : 'Play video'}
      >
        {isBuffering ? null : (
          <div className="bg-black/50 rounded-full p-2 md:p-4 hover:bg-black/70 transition-colors">
            {isPlaying ? (
              <Pause className="md:h-12 md:w-12 h-8 w-8 text-white fill-white" />
            ) : (
              <Play className="md:h-12 md:w-12 h-8 w-8 text-white fill-white" />
            )}
          </div>
        )}
      </button>
    </>
  );
};
